/**
 * <tv-cart-drawer> — panier latéral Tendre Veilleuse.
 *
 * Le HTML vient toujours du serveur (sections/cart-drawer.liquid) : chaque appel
 * Cart AJAX demande `sections: <id>` et les régions [data-cd-region] sont
 * remplacées par la nouvelle version. Les mutations passent par une file unique
 * pour qu'aucune réponse tardive n'écrase un état plus récent.
 *
 * API publique : window.TVCart.add(payload) (formulaire, FormData ou objet JSON),
 * window.TVCart.open(), et l'événement window 'open-cart-drawer'.
 */
(() => {
  if (customElements.get('tv-cart-drawer')) return;

  const GIFT_PROP = '_tv_cadeau_offert';
  const QTY_DEBOUNCE = 250;
  const STALE_AFTER = 10000;
  const GENERIC_ERROR = 'Le panier n’a pas pu être mis à jour. Réessayez dans un instant.';
  const NETWORK_ERROR = 'Connexion impossible. Vérifiez votre réseau puis réessayez.';

  // Veilleuse maternité/allaitement au panier : ne pas proposer une veilleuse concurrente
  // (relance le doute « ai-je pris la bonne ? »), proposer le coussin de grossesse en premier.
  const RIVAL = /veilleuse.*(allaitement|maternit|lumi[eè]re rouge)/i;
  const COUSSIN_HANDLE = 'coussin-de-grossesse-en-u-cale-ventre-dos-et-jambes';

  const EMPTY_STATE = { paidCount: 0, itemCount: 0, anchor: null, productIds: [], titles: [], handles: [], updates: {}, add: [], gifts: [], guideId: null };

  // Même rendu que `money_without_currency` + ' €' (format boutique amount_with_comma_separator).
  const formatMoney = (cents) => {
    const [int, dec] = (cents / 100).toFixed(2).split('.');
    return `${int.replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${dec} €`;
  };

  class CartError extends Error {}

  class TvCartDrawer extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.panel = this.querySelector('[data-cd-panel]');
      this.errorEl = this.querySelector('[data-cd-error]');
      this.statusEl = this.querySelector('[data-cd-status]');
      this.reco = { el: this.querySelector('[data-cd-reco]'), anchor: null, list: [], index: 0, controller: null };

      const root = this.dataset.rootUrl || '/';
      this.urls = {
        root: root.endsWith('/') ? root : root + '/',
        add: this.dataset.cartAddUrl,
        change: this.dataset.cartChangeUrl,
        update: this.dataset.cartUpdateUrl,
      };
      this.sectionId = this.dataset.sectionId;
      this.bumpId = Number(this.dataset.bumpId);

      this.queue = Promise.resolve();
      this.inFlight = 0;
      this.targets = new Map(); // ligne -> quantité voulue, pas encore confirmée
      this.timers = new Map();
      this.lastSyncSig = null;
      this.renderedAt = Date.now();
      this.hasOpened = false;
      this.state = this.readState() || EMPTY_STATE;

      this.addEventListener('click', (e) => this.onClick(e));
      this.addEventListener('change', (e) => this.onChange(e));
      this.addEventListener('submit', (e) => this.onSubmit(e));
      this.dialog.addEventListener('cancel', (e) => { e.preventDefault(); this.close(); });
      this.dialog.addEventListener('close', () => this.afterClose());
      this.dialog.addEventListener('keydown', (e) => this.trapFocus(e));
      window.addEventListener('open-cart-drawer', () => this.open());
      window.addEventListener('pageshow', (e) => { if (e.persisted) this.refresh(); });

      window.TVCart = {
        add: (payload) => this.add(payload),
        open: () => this.open(),
        refresh: () => this.refresh(),
      };

      // Corrections laissées par une session précédente (cadeau orphelin, garantie seule…)
      const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 200));
      idle(() => {
        this.scheduleSync();
        if (this.state.paidCount > 0) this.updateRecos(true);
      });
    }

    /* ---------- Ouverture / fermeture ---------- */

    open() {
      clearTimeout(this.closeTimer);
      if (!this.dialog.open) {
        this.opener = document.activeElement;
        document.body.style.setProperty('--cd-scrollbar', `${window.innerWidth - document.documentElement.clientWidth}px`);
        document.body.classList.add('cart-open');
        this.dialog.showModal();
        this.dialog.getBoundingClientRect(); // fixe l'état initial pour que la transition joue
      }
      this.dialog.classList.add('is-open');
      this.querySelector('[data-focus-id="close"]')?.focus();

      if (!this.hasOpened) {
        this.hasOpened = true;
        this.renderReco();
        this.updateRecos();
      }
      if (Date.now() - this.renderedAt > STALE_AFTER && !this.isBusy()) this.refresh();
    }

    close() {
      if (!this.dialog.open) return;
      this.dialog.classList.remove('is-open');
      const instant = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      clearTimeout(this.closeTimer);
      this.closeTimer = setTimeout(() => this.dialog.close(), instant ? 0 : 400);
    }

    afterClose() {
      clearTimeout(this.closeTimer);
      this.dialog.classList.remove('is-open');
      document.body.classList.remove('cart-open');
      document.body.style.removeProperty('--cd-scrollbar');
      if (this.opener && this.opener.isConnected) this.opener.focus({ preventScroll: true });
      this.opener = null;
    }

    trapFocus(e) {
      if (e.key !== 'Tab') return;
      const focusables = [...this.dialog.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])')]
        .filter((el) => !el.closest('[hidden]') && el.getClientRects().length > 0);
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }

    /* ---------- Événements ---------- */

    onClick(e) {
      const target = e.target.closest('[data-cd-close], [data-cd-qty], [data-cd-reco-step], [data-cd-reco-add]');
      if (!target) return;
      if (target.hasAttribute('data-cd-close')) return this.close();
      if (target.hasAttribute('data-cd-qty')) return this.onQty(target);
      if (target.hasAttribute('data-cd-reco-step')) return this.stepReco(Number(target.dataset.cdRecoStep));
      if (target.hasAttribute('data-cd-reco-add')) return this.addReco(target);
    }

    onChange(e) {
      if (e.target.matches('[data-cd-bump]')) this.toggleBump(e.target);
    }

    // Une modification de quantité encore en attente doit partir avant le checkout.
    onSubmit(e) {
      const form = e.target;
      if (!form.matches('[data-cd-checkout]') || this.allowSubmit || !this.isBusy()) return;
      e.preventDefault();
      const submitter = e.submitter;
      submitter?.setAttribute('aria-busy', 'true');
      this.flushAll().finally(() => {
        this.allowSubmit = true;
        const freshForm = this.querySelector('[data-cd-checkout]');
        if (freshForm && freshForm.requestSubmit) freshForm.requestSubmit(freshForm.querySelector('[name="checkout"]'));
        else window.location.assign(`${this.urls.root}checkout`);
        this.allowSubmit = false;
      });
    }

    /* ---------- Quantités ---------- */

    onQty(button) {
      const line = button.closest('[data-cd-line]');
      if (!line) return;
      const id = line.dataset.cdLine;
      const current = this.targets.has(id) ? this.targets.get(id) : Number(line.dataset.qty);
      const max = line.dataset.max !== undefined ? Number(line.dataset.max) : Infinity;
      const next = Math.max(0, current + Number(button.dataset.cdQty));

      if (next > max) {
        this.showError(`Stock limité : ${max} exemplaire${max > 1 ? 's' : ''} maximum pour cet article.`);
        return;
      }
      this.clearError();
      this.lastSyncSig = null;
      this.targets.set(id, next);
      this.paintTarget(line, next);
      this.paintTotals();

      clearTimeout(this.timers.get(id));
      this.timers.set(id, setTimeout(() => this.flushLine(id), QTY_DEBOUNCE));
    }

    // Montants provisoires au prix unitaire actuel : Shopify confirme ensuite (paliers inclus).
    paintTarget(line, qty) {
      line.classList.add('is-pending');
      line.hidden = qty === 0;
      const price = line.querySelector('[data-cd-price]');
      if (price) price.textContent = formatMoney(Number(line.dataset.unitFinal) * qty);
      const old = line.querySelector('[data-cd-price-old]');
      if (old) {
        const original = Number(line.dataset.unitOriginal) * qty;
        old.textContent = formatMoney(original);
        old.hidden = !(original > Number(line.dataset.unitFinal) * qty);
      }
      const value = line.querySelector('[data-cd-qty-value]');
      if (value) value.textContent = qty;
      const plus = line.querySelector('[data-cd-qty="1"]');
      if (plus && line.dataset.max !== undefined) plus.disabled = qty >= Number(line.dataset.max);
    }

    // Jauge des paliers et sous-total suivent les quantités affichées, avant la réponse serveur.
    paintTotals() {
      let count = this.state.paidCount;
      let subtotal = 0;
      const subtotalEl = this.querySelector('[data-cd-subtotal]');
      this.querySelectorAll('[data-cd-line]').forEach((line) => {
        const id = line.dataset.cdLine;
        const pending = this.targets.has(id);
        const qty = pending ? this.targets.get(id) : Number(line.dataset.qty);
        count += qty - Number(line.dataset.qty);
        subtotal += pending ? Number(line.dataset.unitFinal) * qty : Number(line.dataset.final);
      });
      if (subtotalEl) subtotalEl.textContent = formatMoney(Number(subtotalEl.dataset.rest) + subtotal);

      const progress = this.querySelector('[data-cd-progress]');
      if (!progress) return;
      progress.hidden = count === 0;
      progress.querySelectorAll('[data-cd-step]').forEach((step) => step.classList.toggle('is-active', count >= Number(step.dataset.cdStep)));
      progress.querySelector('.cd-bar-fill').style.width = `${count <= 1 ? 0 : count === 2 ? 50 : 100}%`;
      progress.querySelectorAll('[data-cd-msg]').forEach((msg) => { msg.hidden = Number(msg.dataset.cdMsg) !== Math.min(Math.max(count, 1), 3); });
    }

    flushLine(id) {
      clearTimeout(this.timers.get(id));
      this.timers.delete(id);
      return this.enqueue(async () => {
        if (!this.targets.has(id)) return;
        const qty = this.targets.get(id);
        const line = this.querySelector(`[data-cd-line="${id}"]`);
        if (!line) { this.targets.delete(id); return; }
        if (qty === Number(line.dataset.qty)) {
          this.targets.delete(id);
          line.classList.remove('is-pending');
          return;
        }

        try {
          const data = await this.post(...this.quantityRequest(line, qty));
          if (this.targets.get(id) === qty) this.targets.delete(id);
          await this.render(data.sections);
          const updated = this.querySelector(`[data-cd-line="${id}"]`);
          const served = updated ? Number(updated.dataset.qty) : 0;
          if (!this.targets.has(id) && qty > 0 && served < qty) {
            this.showError(`Stock limité : quantité ajustée à ${served}.`);
          } else {
            this.announce(qty === 0 ? 'Article retiré du panier.' : 'Panier mis à jour.');
          }
        } catch (error) {
          this.targets.delete(id);
          this.showError(error.message);
          await this.refresh(true);
        }
      });
    }

    // Shopify scinde une ligne quand une remise ne couvre qu'une partie de la quantité :
    // on fixe alors la quantité sur la première clé et on vide les autres.
    quantityRequest(line, qty) {
      const keys = [...new Set(line.dataset.keys.split(','))];
      const lines = Number(line.dataset.lines);
      if (keys.length > 1) {
        const updates = Object.fromEntries(keys.map((key, i) => [key, i === 0 ? qty : 0]));
        return [this.urls.update, { updates }];
      }
      if (lines > 1) return [this.urls.update, { updates: { [line.dataset.variantId]: qty } }];
      return [this.urls.change, { id: keys[0], quantity: qty }];
    }

    flushAll() {
      [...this.timers.keys()].forEach((id) => this.flushLine(id));
      return this.queue;
    }

    /* ---------- Ajouts ---------- */

    add(payload) {
      this.clearError();
      this.lastSyncSig = null;
      this.panel.classList.add('is-adding');
      this.open();
      return this.enqueue(async () => {
        try {
          const withGifts = this.withGifts(payload);
          let data;
          try {
            data = await this.post(this.urls.add, withGifts || payload);
          } catch (error) {
            // Un cadeau indisponible ne doit jamais bloquer l'ajout du produit.
            if (!withGifts || error.message === NETWORK_ERROR) throw error;
            data = await this.post(this.urls.add, payload);
          }
          await this.render(data.sections);
          this.announce('Article ajouté au panier.');
          delete data.sections;
          return data;
        } catch (error) {
          this.showError(error.message);
          throw error;
        } finally {
          this.panel.classList.remove('is-adding');
        }
      });
    }

    // Ajoute au même appel les cadeaux connus de la page (snippets/cart-drawer-gifts.liquid),
    // ce qui évite une seconde requête. Renvoie null si rien à ajouter ou payload non convertible.
    withGifts(payload) {
      const items = this.toItems(payload);
      if (!items) return null;
      if (!this.giftMap) {
        try { this.giftMap = JSON.parse(document.getElementById('cd-gift-map')?.textContent || '{}'); } catch { this.giftMap = {}; }
      }
      const present = new Set((this.state.gifts || []).map(String));
      const gifts = [];
      const want = (id) => { id = String(id); if (!present.has(id) && !gifts.includes(id)) gifts.push(id); };
      if (this.state.guideId) want(this.state.guideId);
      items.forEach((item) => (this.giftMap[item.id] || []).forEach(want));
      if (!gifts.length) return null;
      return { items: [...items, ...gifts.map((id) => ({ id: Number(id), quantity: 1, properties: { [GIFT_PROP]: 'true' } }))] };
    }

    toItems(payload) {
      const isForm = payload instanceof HTMLFormElement || payload instanceof FormData;
      if (!isForm) {
        if (payload && Array.isArray(payload.items)) return payload.items;
        return payload && payload.id ? [payload] : null;
      }
      const item = { quantity: 1, properties: {} };
      for (const [key, value] of (payload instanceof FormData ? payload : new FormData(payload))) {
        if (typeof value !== 'string') return null; // fichier joint : on garde le FormData tel quel
        const prop = key.match(/^properties\[(.+)\]$/);
        if (prop) item.properties[prop[1]] = value;
        else if (key === 'id') item.id = Number(value);
        else if (key === 'quantity') item.quantity = Number(value) || 1;
        else if (key === 'selling_plan') item.selling_plan = value;
        else if (/^(items|attributes)\b/.test(key)) return null; // format multi-articles : laissé tel quel
      }
      return item.id ? [item] : null;
    }

    toggleBump(input) {
      const qty = input.checked ? 1 : 0;
      const box = input.closest('[data-cd-bump-box]');
      box?.classList.add('is-loading');
      input.disabled = true;
      this.clearError();
      this.enqueue(async () => {
        try {
          const data = await this.post(this.urls.update, { updates: { [this.bumpId]: qty } });
          await this.render(data.sections);
        } catch (error) {
          this.showError(error.message);
          await this.refresh(true);
        }
      });
    }

    addReco(button) {
      const product = this.visibleRecos()[this.reco.index];
      const variant = product && product.variants.find((v) => v.available);
      if (!variant || button.disabled) return;
      const card = this.querySelector('[data-cd-reco-card]');
      button.disabled = true;
      button.textContent = '…';
      card.classList.add('loading');
      this.clearError();
      this.lastSyncSig = null;
      this.enqueue(async () => {
        try {
          const data = await this.post(this.urls.add, { items: [{ id: variant.id, quantity: 1 }] });
          await this.render(data.sections);
          this.announce('Article ajouté au panier.');
        } catch (error) {
          this.showError(error.message);
        } finally {
          button.disabled = false;
          button.textContent = 'Ajouter';
          card.classList.remove('loading');
          this.renderReco();
        }
      });
    }

    /* ---------- Cadeaux et garantie ---------- */

    needsSync(state) {
      return Object.keys(state.updates).length > 0 || state.add.length > 0;
    }

    // Exécute les corrections et renvoie les sections finales (ou null en cas d'échec).
    async runSync(state) {
      let sections = null;
      if (Object.keys(state.updates).length) {
        sections = (await this.post(this.urls.update, { updates: state.updates })).sections;
        state = this.readState(this.parse(sections)) || state;
      }
      if (state.add.length) {
        const items = state.add.map((id) => ({ id: Number(id), quantity: 1, properties: { [GIFT_PROP]: 'true' } }));
        sections = (await this.post(this.urls.add, { items })).sections;
      }
      return sections;
    }

    scheduleSync() {
      const { updates, add } = this.state;
      if (!Object.keys(updates).length && !add.length) { this.lastSyncSig = null; return; }
      const sig = JSON.stringify([updates, add]);
      if (sig === this.lastSyncSig) return; // déjà tenté sans succès : on ne boucle pas
      this.lastSyncSig = sig;

      this.enqueue(async () => {
        try {
          const sections = await this.runSync(this.state);
          if (sections) await this.render(sections, { sync: false });
        } catch (error) {
          console.warn('[tv-cart-drawer] synchronisation cadeaux/garantie :', error.message);
        }
      });
    }

    /* ---------- Réseau ---------- */

    enqueue(task) {
      const run = this.queue.then(async () => {
        this.setBusy(1);
        try { return await task(); } finally { this.setBusy(-1); }
      });
      this.queue = run.catch(() => {});
      return run;
    }

    setBusy(delta) {
      this.inFlight += delta;
      this.panel.setAttribute('aria-busy', this.inFlight > 0 ? 'true' : 'false');
    }

    isBusy() {
      return this.inFlight > 0 || this.timers.size > 0 || this.targets.size > 0;
    }

    async post(url, payload) {
      let body;
      let headers = { Accept: 'application/json' };
      if (payload instanceof HTMLFormElement || payload instanceof FormData) {
        body = new FormData();
        const source = payload instanceof FormData ? payload : new FormData(payload);
        for (const [k, v] of source) body.append(k, v);
        body.append('sections', this.sectionId);
        body.append('sections_url', window.location.pathname);
      } else {
        headers = { ...headers, 'Content-Type': 'application/json' };
        body = JSON.stringify({ ...payload, sections: this.sectionId, sections_url: window.location.pathname });
      }

      let response;
      try {
        response = await fetch(url, { method: 'POST', headers, body });
      } catch {
        throw new CartError(NETWORK_ERROR);
      }
      const data = await response.json().catch(() => null);
      if (!response.ok || !data) {
        if (response.status === 429) throw new CartError('Trop de demandes à la suite. Patientez un instant puis réessayez.');
        throw new CartError((data && (data.description || data.message)) || GENERIC_ERROR);
      }
      return data;
    }

    // `inQueue` : appel depuis une tâche déjà dans la file (évite l'interblocage).
    refresh(inQueue = false) {
      const task = async () => {
        try {
          const response = await fetch(`${this.urls.root}?sections=${encodeURIComponent(this.sectionId)}`, { headers: { Accept: 'application/json' } });
          if (!response.ok) throw new Error(String(response.status));
          await this.render(await response.json(), { fallback: false });
        } catch (error) {
          console.warn('[tv-cart-drawer] rafraîchissement impossible :', error.message);
        }
      };
      return inQueue ? task() : this.enqueue(task);
    }

    /* ---------- Rendu ---------- */

    // Appelé depuis la file uniquement. Section absente de la réponse : relecture complète.
    async render(sections, { sync = true, fallback = true } = {}) {
      const html = sections && sections[this.sectionId];
      if (!html) {
        if (fallback) await this.refresh(true);
        return;
      }
      let doc = this.parse(sections);
      const pending = this.readState(doc);
      if (sync && pending && this.needsSync(pending)) {
        const sig = JSON.stringify([pending.updates, pending.add]);
        if (sig !== this.lastSyncSig) {
          this.lastSyncSig = sig;
          try {
            const synced = await this.runSync(pending);
            if (synced && synced[this.sectionId]) doc = this.parse(synced);
          } catch (error) {
            console.warn('[tv-cart-drawer] synchronisation cadeaux/garantie :', error.message);
          }
        }
      }
      const active = document.activeElement;
      const hadFocus = this.contains(active);
      const focusId = hadFocus ? active.closest('[data-focus-id]')?.dataset.focusId : null;
      const oldFill = this.querySelector('.cd-bar-fill')?.style.width;

      this.querySelectorAll('[data-cd-region]').forEach((region) => {
        const fresh = doc.querySelector(`[data-cd-region="${region.dataset.cdRegion}"]`);
        if (!fresh) return;
        region.replaceChildren(...fresh.childNodes);
        region.hidden = fresh.hidden;
      });

      // La barre est un nouvel élément : on rejoue la transition depuis l'ancienne largeur.
      const fill = this.querySelector('.cd-bar-fill');
      if (fill && oldFill && fill.style.width !== oldFill) {
        const to = fill.style.width;
        fill.style.width = oldFill;
        fill.getBoundingClientRect();
        fill.style.width = to;
      }

      // Quantités cliquées pendant la requête : elles restent affichées jusqu'à leur envoi.
      this.targets.forEach((qty, id) => {
        const line = this.querySelector(`[data-cd-line="${id}"]`);
        if (line) this.paintTarget(line, qty);
      });

      if (hadFocus && !active.isConnected && this.dialog.open) {
        const el = focusId && this.querySelector(`[data-focus-id="${focusId}"]`);
        (el && !el.disabled ? el : this.querySelector('[data-focus-id="close"]'))?.focus({ preventScroll: true });
      }

      this.state = this.readState() || this.state;
      if (this.targets.size) this.paintTotals();
      this.renderedAt = Date.now();
      this.syncHeader();
      this.updateRecos();
      if (!this.needsSync(this.state)) this.lastSyncSig = null;
    }

    parse(sections) {
      return new DOMParser().parseFromString(sections[this.sectionId], 'text/html');
    }

    readState(root = this) {
      try {
        return JSON.parse(root.querySelector('[data-cd-state]').textContent);
      } catch {
        return null;
      }
    }

    // Pastille et libellé du bouton panier de l'en-tête (sections/header.liquid).
    syncHeader() {
      const button = document.getElementById('cart-btn');
      if (!button) return;
      const count = this.state.itemCount;
      let badge = button.querySelector('.cart-badge');
      if (count > 0) {
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'cart-badge';
          badge.setAttribute('aria-hidden', 'true');
          button.append(badge);
        }
        badge.textContent = count;
      } else {
        badge?.remove();
      }
      button.setAttribute('aria-label', count > 0 ? `Panier (${count} article${count > 1 ? 's' : ''})` : 'Panier');
    }

    showError(message) {
      this.errorEl.textContent = message || GENERIC_ERROR;
      this.errorEl.hidden = false;
    }

    clearError() {
      this.errorEl.hidden = true;
      this.errorEl.textContent = '';
    }

    announce(message) {
      this.statusEl.textContent = '';
      requestAnimationFrame(() => { this.statusEl.textContent = message; });
    }

    /* ---------- Recommandations ---------- */

    // `prefetch` : chargement anticipé, avant la première ouverture.
    updateRecos(prefetch = false) {
      if (!this.hasOpened && !prefetch) return;
      const { anchor, paidCount } = this.state;
      if (!paidCount || !anchor) { this.reco.el.hidden = true; return; }
      if (anchor === this.reco.anchor) { this.renderReco(); return; }

      this.reco.anchor = anchor;
      this.reco.list = [];
      this.reco.index = 0;
      this.reco.controller?.abort();
      this.reco.controller = new AbortController();
      fetch(`${this.urls.root}recommendations/products.json?product_id=${anchor}&limit=6&intent=related`, { signal: this.reco.controller.signal })
        .then((r) => (r.ok ? r.json() : { products: [] }))
        .then(async (data) => {
          let list = data.products || [];
          if (this.hasMaternite() && !this.state.handles.includes(COUSSIN_HANDLE)) {
            const coussin = await fetch(`${this.urls.root}products/${COUSSIN_HANDLE}.js`, { signal: this.reco.controller.signal })
              .then((r) => (r.ok ? r.json() : null))
              .catch((error) => { if (error.name === 'AbortError') throw error; return null; });
            if (coussin) list = [coussin, ...list.filter((p) => p.id !== coussin.id)];
          }
          // La garantie a déjà sa case à cocher : jamais en recommandation.
          this.reco.list = list.filter((p) => p.available && p.variants.some((v) => v.available)
            && !p.variants.some((v) => v.id === this.bumpId));
          this.renderReco();
        })
        .catch((error) => {
          if (error.name !== 'AbortError') console.warn('[tv-cart-drawer] recommandations :', error.message);
        });
    }

    hasMaternite() {
      return this.state.titles.some((title) => RIVAL.test(title));
    }

    visibleRecos() {
      const inCart = new Set(this.state.productIds);
      const maternite = this.hasMaternite();
      return this.reco.list.filter((p) => !inCart.has(p.id) && !(maternite && RIVAL.test(p.title)));
    }

    stepReco(step) {
      const count = this.visibleRecos().length;
      if (!count) return;
      this.reco.index = (this.reco.index + step + count) % count;
      this.renderReco();
    }

    renderReco() {
      const list = this.visibleRecos();
      const el = this.reco.el;
      if (!this.state.paidCount || !list.length) { el.hidden = true; return; }
      if (this.reco.index >= list.length) this.reco.index = 0;
      const product = list[this.reco.index];
      const variant = product.variants.find((v) => v.available) || product.variants[0];

      const img = el.querySelector('[data-cd-reco-img]');
      if (product.featured_image) {
        const src = product.featured_image;
        img.src = src + (src.includes('?') ? '&' : '?') + 'width=96';
        img.alt = product.title;
        img.hidden = false;
      } else {
        img.hidden = true;
      }
      const link = el.querySelector('[data-cd-reco-link]');
      link.href = product.url;
      link.textContent = product.title;
      el.querySelector('[data-cd-reco-price]').textContent = formatMoney(variant.price);
      const old = el.querySelector('[data-cd-reco-old]');
      const compare = variant.compare_at_price || product.compare_at_price;
      old.hidden = !(compare > variant.price);
      old.textContent = old.hidden ? '' : formatMoney(compare);
      el.querySelector('[data-cd-reco-add]').setAttribute('aria-label', `Ajouter ${product.title} au panier`);
      el.hidden = false;
    }
  }

  customElements.define('tv-cart-drawer', TvCartDrawer);
})();
